import { Center, Loader, Stack, Text } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'

import { useGlobalState } from '@/hooks/useGlobalState'
import { formatDate } from '@/utils/dates'

import BillingChart from './BillingChart'
import { BillingStatsPanel } from './BillingStatsPanel'

export function UsageTab() {
  const { api } = useGlobalState()

  const billingInfoQuery = useQuery({
    queryKey: ['billing', 'info'],
    queryFn: () => api.getBillingInfo(),
  })

  // Calculate 30-day periods starting from today
  const [periodOffset, setPeriodOffset] = useState<number>(0) // 0 = current period, 1 = previous 30 days, etc.

  const currentPeriodDates = useMemo(() => {
    const now = new Date()
    const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    // Move back by periodOffset * 30 days
    endDate.setDate(endDate.getDate() - periodOffset * 30)

    const startDate = new Date(endDate)
    startDate.setDate(startDate.getDate() - 29) // 30 days total (including end date)

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    }
  }, [periodOffset])

  const billingInfo = billingInfoQuery.data?.billingInfo

  // Query for billing credits data for selected period
  const billingCreditsQuery = useQuery({
    queryKey: ['billing', 'credits', currentPeriodDates.startDate, currentPeriodDates.endDate],
    queryFn: () =>
      api.getBillingCreditsData({
        start_date: currentPeriodDates.startDate,
        end_date: currentPeriodDates.endDate,
      }),
    enabled: !!billingInfo, // Only need billing info to exist, not specific invoice data
  })

  // Navigation handlers
  const navigateToPreviousPeriod = useCallback(() => {
    setPeriodOffset(prev => prev + 1) // Go further back in time
  }, [])

  const navigateToNextPeriod = useCallback(() => {
    setPeriodOffset(prev => Math.max(0, prev - 1)) // Go forward in time, but not beyond current period
  }, [])

  // Navigation state
  const canGoBack = true // Can always go back to previous 30-day periods
  const canGoForward = periodOffset > 0 // Can only go forward if we're not already at current period

  // Loading and error states for usage tab
  const isLoadingCredits = billingCreditsQuery.isLoading
  const billingCreditsError = billingCreditsQuery.error

  const hasCreditsData = billingCreditsQuery.data && !isLoadingCredits && !billingCreditsError

  return (
    <Stack gap="xl">
      {/* Billing Stats Panel */}
      {hasCreditsData && (
        <BillingStatsPanel
          data={billingCreditsQuery.data}
          onNavigateToPrevious={navigateToPreviousPeriod}
          onNavigateToNext={navigateToNextPeriod}
          canGoBack={canGoBack}
          canGoForward={canGoForward}
          startDate={currentPeriodDates.startDate}
          endDate={currentPeriodDates.endDate}
        />
      )}

      {/* Loading state for credits data */}
      {isLoadingCredits && (
        <Center h={400}>
          <Loader size="lg" />
        </Center>
      )}

      {/* Error state for credits data */}
      {billingCreditsError && <Text c="red">Chart Error: {billingCreditsError.message}</Text>}

      {/* Billing Chart */}
      {hasCreditsData && (
        <BillingChart
          data={billingCreditsQuery.data}
          startDate={currentPeriodDates.startDate}
          endDate={currentPeriodDates.endDate}
        />
      )}

      {/* Usage Info */}
      <Stack gap="md" mt="xl">
        {/* Data disclaimer */}
        {billingInfo?.lastProcessedDate && (
          <Text size="sm" c="dimmed" ta="center" style={{ fontStyle: 'italic' }}>
            All billing data shown in UTC timezone • Last updated:{' '}
            {formatDate(billingInfo.lastProcessedDate, {
              format: 'MMM D, YYYY HH:mm [UTC]',
              fallback: 'Date not available',
              utc: true,
            })}
          </Text>
        )}

        {/* Support contact */}
        <Text size="sm" c="dimmed" ta="center">
          Have questions about your billing? Contact us at{' '}
          <Text component="a" href="mailto:support@sidekickwellness.com" c="blue" td="underline">
            support@sidekickwellness.com
          </Text>
        </Text>
      </Stack>
    </Stack>
  )
}
